<template>
  <div class="qp-shell" :style="cssVars">
    <header class="qp-header">
      <div class="qp-header-left">
        <div class="qp-logo-mark"></div>
        <span class="qp-system-name">{{ meta?.systemName || 'Obra Tycoon' }}</span>
      </div>
      <span v-if="participantName && stage !== 'name'" class="qp-user">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="4.5" r="2.2" stroke="currentColor" stroke-width="1.2"/><path d="M2 11c0-2.21 1.79-4 4-4s4 1.79 4 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
        {{ participantName }}
      </span>
    </header>

    <main class="qp-main">
      <div v-if="loading" class="qp-state">
        <div class="qp-spinner"></div>
        <p>Carregando questionário…</p>
      </div>

      <div v-else-if="error" class="qp-state qp-state--error">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="currentColor" stroke-width="2"/><path d="M20 12v10M20 27v2" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
        <p>{{ error }}</p>
      </div>

      <template v-else-if="meta">
        <!-- Identificação -->
        <section v-if="stage === 'name'" class="qp-card qp-intro">
          <h1 class="qp-title">{{ meta.title }}</h1>
          <p v-if="meta.description" class="qp-desc">{{ meta.description }}</p>
          <div class="qp-facts">
            <span>{{ meta.questionCount }} pergunta{{ meta.questionCount === 1 ? '' : 's' }}</span>
            <span v-if="meta.categories.length > 1">· {{ meta.categories.length }} etapas</span>
            <span>· múltipla escolha</span>
          </div>
          <ol v-if="meta.categories.length > 1" class="qp-cat-list">
            <li v-for="(c, i) in meta.categories" :key="i">
              <span class="qp-cat-num">{{ i + 1 }}</span>
              <span class="qp-cat-name">{{ c.name }}</span>
              <span class="qp-cat-count">{{ c.questionCount }} pergunta{{ c.questionCount === 1 ? '' : 's' }}</span>
            </li>
          </ol>
          <form class="qp-name-form" @submit.prevent="start">
            <p v-if="savedName && participantName === savedName" class="qp-welcome">
              Bem-vindo de volta! Você vai responder como <strong>{{ savedName }}</strong> — altere abaixo se quiser.
            </p>
            <label class="qp-field">
              <span class="qp-label-row">
                <span class="qp-label">Nome de usuário</span>
                <span class="qp-counter" :class="{ 'qp-counter--max': participantName.length >= NAME_MAX }">{{ participantName.length }}/{{ NAME_MAX }}</span>
              </span>
              <input
                ref="nameInputRef"
                v-model="participantName"
                class="qp-input"
                :maxlength="NAME_MAX"
                autocomplete="name"
                placeholder="Como você quer ser identificado"
              />
            </label>
            <p v-if="startError" class="qp-error">{{ startError }}</p>
            <button class="qp-btn" type="submit" :disabled="starting || meta.questionCount === 0">
              {{ starting ? 'Preparando…' : 'Começar questionário' }}
            </button>
            <p v-if="meta.questionCount === 0" class="qp-error">Este questionário ainda não tem perguntas.</p>
          </form>
        </section>

        <!-- Prova / resumo -->
        <section v-else>
          <div class="qp-quiz-head">
            <h1 class="qp-title">{{ meta.title }}</h1>
          </div>
          <QuizRunner
            :categories="categories"
            :current-index="currentIndex"
            :result="result"
            :submitting="submitting"
            :participant-name="participantName"
            @submit-category="submitCategory"
            @restart="start"
          />
        </section>
      </template>
    </main>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'
import { getQuizDeviceId, getSavedParticipantName, saveParticipantName } from '@/utils/quizDevice'
import QuizRunner, { QuizRunnerCategory, QuizRunnerResult } from '@/components/office/QuizRunner.vue'

interface PublicQuizMeta {
  title: string; description: string; questionCount: number
  categories: { name: string; questionCount: number }[]
  systemName: string; primaryColor: string
}
type SubmitResponse = ({ done: false; nextCategoryId: string } | ({ done: true } & QuizRunnerResult))

// mesmo limite do backend (sanitizeParticipantName)
const NAME_MAX = 40

export default defineComponent({
  name: 'QuizPublicView',
  components: { QuizRunner },
  setup() {
    const route = useRoute()
    const hash = computed(() => `${route.params.hash || ''}`)
    const base = computed(() => `/quiz-public/${encodeURIComponent(hash.value)}`)

    const loading = ref(true)
    const error = ref('')
    const meta = ref<PublicQuizMeta | null>(null)
    const stage = ref<'name' | 'quiz'>('name')
    const savedName = getSavedParticipantName().slice(0, NAME_MAX)
    const participantName = ref(savedName)
    const nameInputRef = ref<HTMLInputElement | null>(null)
    const starting = ref(false)
    const startError = ref('')
    const attemptId = ref('')
    const categories = ref<QuizRunnerCategory[]>([])
    const currentIndex = ref(0)
    const result = ref<QuizRunnerResult | null>(null)
    const submitting = ref(false)

    const cssVars = computed(() => (meta.value?.primaryColor ? { '--system-navbar': meta.value.primaryColor } : {}))

    const load = async () => {
      loading.value = true
      try {
        meta.value = await apiFetch<PublicQuizMeta>(base.value, undefined, false)
        document.title = meta.value.title
        await nextTick()
        nameInputRef.value?.focus()
      } catch (e: unknown) {
        error.value = e instanceof Error ? e.message : 'Questionário indisponível.'
      } finally { loading.value = false }
    }

    const start = async () => {
      const name = participantName.value.replace(/\s+/g, ' ').trim()
      if (name.length < 2 || name.length > NAME_MAX) { startError.value = `Informe um nome de usuário entre 2 e ${NAME_MAX} caracteres.`; return }
      if ((name.match(/[\p{L}\p{N}]/gu) || []).length < 2) { startError.value = 'O nome precisa ter pelo menos 2 letras ou números.'; return }
      starting.value = true; startError.value = ''
      try {
        const res = await apiFetch<{ attemptId: string; categories: QuizRunnerCategory[] }>(
          `${base.value}/start`,
          { method: 'POST', body: JSON.stringify({ participantName: name, deviceId: getQuizDeviceId() }) },
          false
        )
        participantName.value = name
        saveParticipantName(name)
        attemptId.value = res.attemptId
        categories.value = res.categories
        currentIndex.value = 0
        result.value = null
        stage.value = 'quiz'
        window.scrollTo({ top: 0 })
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Erro ao iniciar.'
        if (stage.value === 'name') startError.value = msg
        else uiStore.toast(msg, 'error')
      } finally { starting.value = false }
    }

    // Envia a categoria atual; o servidor só corrige a prova no envio da última.
    const submitCategory = async (payload: { categoryId: string; answers: Record<string, number> }) => {
      submitting.value = true
      try {
        const res = await apiFetch<SubmitResponse>(
          `${base.value}/submit`,
          { method: 'POST', body: JSON.stringify({ attemptId: attemptId.value, ...payload }) },
          false
        )
        if (res.done) result.value = res
        else currentIndex.value += 1
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (e: unknown) {
        uiStore.toast(e instanceof Error ? e.message : 'Erro ao enviar respostas.', 'error')
      } finally { submitting.value = false }
    }

    onMounted(load)

    return { NAME_MAX, loading, error, meta, stage, savedName, participantName, nameInputRef, starting, startError, categories, currentIndex, result, submitting, cssVars, start, submitCategory }
  }
})
</script>

<style scoped lang="scss">
.qp-shell {
  min-height: 100vh; display: flex; flex-direction: column;
  background: var(--bg, #f3f2f1); color: var(--ink, #201f1e);
  font-family: var(--font-body, Inter, 'Segoe UI', sans-serif);
}
.qp-header {
  height: var(--navbar-h, 48px); flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 0 20px; background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff);
}
.qp-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.qp-logo-mark { width: 18px; height: 18px; border-radius: 4px; background: rgba(255,255,255,.85); flex-shrink: 0; }
.qp-system-name { font-size: 14px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.qp-user {
  display: inline-flex; align-items: center; gap: 6px; max-width: 50%;
  padding: 4px 10px; border-radius: 12px; background: rgba(255,255,255,.14);
  font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.qp-main { flex: 1; width: 100%; max-width: 820px; margin: 0 auto; padding: 28px 16px 40px; box-sizing: border-box; }

.qp-state {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 80px 20px; color: var(--ink-muted, #605e5c); text-align: center; font-size: 14px;
  &--error { color: var(--danger, #a4262c); }
}
.qp-spinner {
  width: 28px; height: 28px; border-radius: 50%;
  border: 3px solid var(--border, rgba(0,0,0,.1)); border-top-color: var(--system-navbar, #0f4c81);
  animation: qp-spin .8s linear infinite;
}
@keyframes qp-spin { to { transform: rotate(360deg); } }

.qp-card {
  background: var(--card, #fff); border: 1px solid var(--border, rgba(0,0,0,.1));
  border-radius: var(--radius-lg, 10px); box-shadow: var(--shadow-sm);
}
.qp-intro { max-width: 540px; margin: 24px auto 0; padding: 28px; border-top: 4px solid var(--system-navbar, #0f4c81); }
.qp-title { margin: 0 0 8px; font-size: 22px; font-weight: 700; color: var(--ink, #201f1e); }
.qp-desc { margin: 0 0 12px; font-size: 14px; line-height: 1.5; color: var(--ink-muted, #605e5c); white-space: pre-wrap; }
.qp-facts { display: flex; gap: 4px; flex-wrap: wrap; font-size: 12px; color: var(--ink-muted, #605e5c); margin-bottom: 16px; }
.qp-cat-list {
  margin: 0 0 20px; padding: 0; list-style: none;
  border: 1px solid var(--border, rgba(0,0,0,.1)); border-radius: var(--radius, 6px); overflow: hidden;
  li { display: flex; align-items: center; gap: 10px; padding: 8px 12px; font-size: 13px; }
  li + li { border-top: 1px solid var(--border, rgba(0,0,0,.08)); }
}
.qp-cat-num {
  width: 22px; height: 22px; border-radius: 6px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81); font-size: 11px; font-weight: 700;
}
.qp-cat-name { flex: 1; min-width: 0; font-weight: 600; color: var(--ink, #201f1e); }
.qp-cat-count { font-size: 12px; color: var(--ink-muted, #605e5c); white-space: nowrap; }
.qp-name-form { display: flex; flex-direction: column; gap: 12px; }
.qp-welcome {
  margin: 0; padding: 9px 12px; border-radius: var(--radius, 6px);
  background: var(--accent-light, #deecf9); font-size: 13px; color: var(--ink, #201f1e);
}
.qp-field { display: flex; flex-direction: column; gap: 5px; }
.qp-label { font-size: 12px; font-weight: 600; color: var(--ink-muted, #605e5c); }
.qp-label-row { display: flex; justify-content: space-between; align-items: baseline; }
.qp-counter { font-size: 11px; color: var(--ink-faint, #a19f9d); font-variant-numeric: tabular-nums; &--max { color: var(--warning, #d83b01); } }
.qp-input {
  padding: 10px 12px; border-radius: var(--radius, 6px); border: 1.5px solid var(--border-strong, rgba(0,0,0,.2));
  font-size: 14px; outline: none; background: var(--surface, #fff); color: var(--ink, #201f1e); font-family: inherit;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}
.qp-error { margin: 0; font-size: 12px; color: var(--danger, #a4262c); }
.qp-btn {
  padding: 10px 18px; border-radius: var(--radius, 6px); border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 14px; font-weight: 600;
  &:hover:not(:disabled) { opacity: .9; }
  &:disabled { opacity: .6; cursor: not-allowed; }
}
.qp-quiz-head { margin-bottom: 14px; }

@media (max-width: 600px) {
  .qp-main { padding: 16px 12px 32px; }
  .qp-intro { padding: 20px; margin-top: 8px; }
  .qp-title { font-size: 19px; }
}
</style>
